(() => {
  "use strict";

  const ROOT = window.Shopify?.routes?.root || "/";
  const originalFetch = window.fetch.bind(window);

  let syncRunning = false;
  let syncRequested = false;
  let syncTimer = null;

  const urls = {
    cart: `${ROOT}cart.js`,
    add: `${ROOT}cart/add.js`,
    change: `${ROOT}cart/change.js`,
  };

  async function shopifyRequest(url, options = {}) {
    const response = await originalFetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(options.body
          ? { "Content-Type": "application/json" }
          : {}),
        ...(options.headers || {}),
      },
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        `Koller-Pfand: Shopify-Fehler ${response.status}: ${errorText}`
      );
    }

    return response.json();
  }

  async function loadCart() {
    return shopifyRequest(urls.cart);
  }

  function isAutomaticDeposit(item) {
    return (
      String(item.properties?._koller_auto_pfand || "") === "1"
    );
  }

  function readDepositMapFromPage() {
    window.KollerPfandMap = window.KollerPfandMap || {};

    document
      .querySelectorAll(".koller-pfand-map")
      .forEach((element) => {
        const variantId = String(
          element.dataset.variantId || ""
        ).trim();

        const depositVariantId = String(
          element.dataset.pfandVariantId || ""
        ).trim();

        if (variantId && depositVariantId) {
          window.KollerPfandMap[variantId] = depositVariantId;
        }
      });

    return window.KollerPfandMap;
  }

  function calculateRequiredDeposits(cart) {
    const required = new Map();
    const depositMap = readDepositMapFromPage();

    for (const item of cart.items) {
      if (isAutomaticDeposit(item)) {
        continue;
      }

      const propertyDepositId = String(
        item.properties?._koller_pfand_variant_id || ""
      ).trim();

      const mappedDepositId = String(
        depositMap[String(item.variant_id)] || ""
      ).trim();

      const depositVariantId =
        propertyDepositId || mappedDepositId;

      if (!depositVariantId) {
        continue;
      }

      const currentQuantity =
        required.get(depositVariantId) || 0;

      required.set(
        depositVariantId,
        currentQuantity + Number(item.quantity || 0)
      );
    }

    return required;
  }

  function findExistingDeposits(cart) {
    const existing = new Map();

    for (const item of cart.items) {
      if (!isAutomaticDeposit(item)) {
        continue;
      }

      const variantId = String(item.variant_id);

      if (!existing.has(variantId)) {
        existing.set(variantId, []);
      }

      existing.get(variantId).push(item);
    }

    return existing;
  }

  async function changeCartLine(lineKey, quantity) {
    return shopifyRequest(urls.change, {
      method: "POST",
      body: JSON.stringify({
        id: lineKey,
        quantity,
      }),
    });
  }

  async function addDeposits(items) {
    if (items.length === 0) {
      return;
    }

    await shopifyRequest(urls.add, {
      method: "POST",
      body: JSON.stringify({ items }),
    });
  }

  async function synchronizeDeposits() {
    if (syncRunning) {
      syncRequested = true;
      return;
    }

    syncRunning = true;
    syncRequested = false;

    try {
      const cart = await loadCart();
      const required = calculateRequiredDeposits(cart);
      const existing = findExistingDeposits(cart);

      let cartChanged = false;

      /*
       * Vorhandene Pfandpositionen anpassen oder entfernen.
       */
      for (const [variantId, lines] of existing.entries()) {
        const requiredQuantity = required.get(variantId) || 0;
        const primaryLine = lines[0];

        if (Number(primaryLine.quantity) !== requiredQuantity) {
          await changeCartLine(primaryLine.key, requiredQuantity);
          cartChanged = true;
        }

        /*
         * Doppelte automatische Pfandzeilen entfernen.
         */
        for (const duplicateLine of lines.slice(1)) {
          await changeCartLine(duplicateLine.key, 0);
          cartChanged = true;
        }
      }

      /*
       * Fehlende Pfandpositionen hinzufügen.
       */
      const missingDeposits = [];

      for (const [variantId, quantity] of required.entries()) {
        if (quantity > 0 && !existing.has(variantId)) {
          missingDeposits.push({
            id: Number(variantId),
            quantity,
            properties: {
              _koller_auto_pfand: "1",
            },
          });
        }
      }

      if (missingDeposits.length > 0) {
        await addDeposits(missingDeposits);
        cartChanged = true;
      }

      if (cartChanged) {
        document.dispatchEvent(
          new CustomEvent("koller:cart-updated", {
            bubbles: true,
          })
        );

        /*
         * Auf der normalen Warenkorbseite neu laden,
         * damit Mengen und Reihenfolge sofort stimmen.
         */
        if (
          window.location.pathname === "/cart" ||
          window.location.pathname.endsWith("/cart")
        ) {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error(
        "Koller-Pfand konnte nicht synchronisiert werden:",
        error
      );
    } finally {
      syncRunning = false;

      if (syncRequested) {
        scheduleSync(150);
      }
    }
  }

  function scheduleSync(delay = 350) {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      synchronizeDeposits();
    }, delay);
  }

  function getRequestUrl(input) {
    if (typeof input === "string") {
      return input;
    }

    if (input instanceof Request) {
      return input.url;
    }

    return "";
  }

  function modifiesCart(url) {
    return /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(
      url
    );
  }

  /*
   * Dawn-Warenkorbänderungen erkennen.
   * Nur ein einziger Fetch-Wrapper.
   */
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const requestUrl = getRequestUrl(args[0]);

    if (modifiesCart(requestUrl)) {
      scheduleSync();
    }

    return response;
  };

  /*
   * Pfandpositionen in der sichtbaren Darstellung
   * immer an das Ende verschieben.
   */
  function moveDepositRowsToBottom() {
    document
      .querySelectorAll("table.cart-items tbody")
      .forEach((tableBody) => {
        tableBody
          .querySelectorAll("tr.koller-pfand-item")
          .forEach((depositRow) => {
            tableBody.appendChild(depositRow);
          });
      });
  }

  let sortingTimer = null;

  function scheduleDepositSorting(delay = 50) {
    window.clearTimeout(sortingTimer);

    sortingTimer = window.setTimeout(() => {
      moveDepositRowsToBottom();
    }, delay);
  }

  const sortingObserver = new MutationObserver(() => {
    scheduleDepositSorting();
  });

  function initializeKollerPfand() {
    readDepositMapFromPage();
    scheduleSync(500);
    scheduleDepositSorting(600);

    sortingObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initializeKollerPfand
    );
  } else {
    initializeKollerPfand();
  }

  document.addEventListener(
    "koller:cart-updated",
    () => {
      scheduleDepositSorting(150);
    }
  );
})();